/**
 * P2P WebRTC — 1v1 ning BIRLAMCHI yo'li (D-035).
 *
 * Signaling REST orqali yuboriladi, WS orqali keladi (D-080). Bu yerdagi
 * kod `POST /match/{id}/signal` chaqiradi va `rtc.signal` eventlarini
 * `handleSignal()` orqali qabul qiladi — WS ulanishining o'zi ilova
 * darajasida (RealtimeProvider).
 *
 * **TURN YO'Q** (D-036). Faqat STUN. Simmetrik NAT ortida ICE
 * muvaffaqiyatsiz tugaydi va chaqiruvchi LiveKit'ga o'tadi — aynan shu
 * uchun `onState('failed')` jim emas, majburiy signal.
 */

import { request } from '../api/client';
import { AUDIO_ONLY, type Transport, type VoiceHandlers, type VoiceSession } from './index';

interface P2POptions {
  matchId: number;
  /** Kim birinchi `offer` yuboradi — quyida izohi bor. */
  isOfferer: boolean;
  stunUrls: string[];
  handlers: VoiceHandlers;
}

/**
 * Kim `offer` yaratadi.
 *
 * Ikkalasi ham yaratsa "glare" bo'ladi — ikkita offer to'qnashadi va
 * ulanish qurilmaydi. Qoida DETERMINISTIK bo'lishi kerak va ikkala
 * tomonda BIR XIL natija berishi shart: kichik `id` — offerer.
 */
export function isOfferer(myUserId: number, peerUserId: number): boolean {
  return myUserId < peerUserId;
}

export class P2PSession implements VoiceSession {
  readonly transport: Transport = 'p2p';
  iceMs: number | null = null;
  reconnects = 0;

  private pc: RTCPeerConnection | null = null;
  private local: MediaStream | null = null;
  private startedAt = 0;
  /** Remote description kelmasdan oldin tushgan nomzodlar. */
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private closed = false;

  constructor(private readonly options: P2POptions) {}

  async start(): Promise<void> {
    this.startedAt = performance.now();
    this.options.handlers.onState('connecting');

    this.local = await navigator.mediaDevices.getUserMedia(AUDIO_ONLY);

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: this.options.stunUrls }],
    });
    this.pc = pc;

    for (const track of this.local.getTracks()) pc.addTrack(track, this.local);

    pc.ontrack = (event) => {
      const [stream] = event.streams;
      if (stream) this.options.handlers.onRemoteStream(stream);
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) void this.send('candidate', event.candidate.toJSON());
    };

    pc.onconnectionstatechange = () => {
      if (this.closed) return;
      switch (pc.connectionState) {
        case 'connected':
          // ICE vaqti M-001 uchun — bir marta o'lchanadi.
          this.iceMs ??= Math.round(performance.now() - this.startedAt);
          this.options.handlers.onState('connected');
          break;
        case 'disconnected':
          this.reconnects += 1;
          this.options.handlers.onState('reconnecting');
          break;
        case 'failed':
          // Jim qolmaymiz — chaqiruvchi shu signal bo'yicha LiveKit'ga
          // o'tadi (D-036).
          this.options.handlers.onState('failed');
          break;
        default:
          break;
      }
    };

    if (this.options.isOfferer) {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await this.send('offer', { sdp: offer.sdp, type: offer.type });
    }
  }

  /** WS'dan kelgan signal. `RealtimeProvider` uni shu yerga uzatadi. */
  async handleSignal(kind: string, payload: Record<string, unknown>): Promise<void> {
    const pc = this.pc;
    if (!pc || this.closed) return;

    if (kind === 'offer') {
      await pc.setRemoteDescription(payload as unknown as RTCSessionDescriptionInit);
      await this.drainCandidates();
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await this.send('answer', { sdp: answer.sdp, type: answer.type });
      return;
    }

    if (kind === 'answer') {
      await pc.setRemoteDescription(payload as unknown as RTCSessionDescriptionInit);
      await this.drainCandidates();
      return;
    }

    if (kind === 'candidate') {
      // `unknown` orqali: `payload` serverdan kelgan ochiq shakl va uni
      // brauzer tuzilmasiga to'g'ridan-to'g'ri qo'yib bo'lmaydi.
      const candidate = payload as unknown as RTCIceCandidateInit;
      // Nomzod remote description'dan OLDIN kelishi mumkin — signaling
      // tartibi kafolatlanmagan. Bunday nomzodni tashlab yuborish
      // ulanishni sekinlashtiradi yoki umuman buzadi.
      if (pc.remoteDescription) await pc.addIceCandidate(candidate);
      else this.pendingCandidates.push(candidate);
    }
  }

  private async drainCandidates(): Promise<void> {
    const queued = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const candidate of queued) {
      await this.pc?.addIceCandidate(candidate);
    }
  }

  private async send(kind: string, payload: unknown): Promise<void> {
    if (this.closed) return;
    try {
      await request(`/match/${this.options.matchId}/signal`, {
        method: 'POST',
        body: { kind, payload },
      });
    } catch {
      // Qo'ng'iroq tugagan bo'lsa server 409 beradi — bu kutilgan holat
      // va ulanishni yiqitmasligi kerak.
    }
  }

  setMuted(muted: boolean): void {
    for (const track of this.local?.getAudioTracks() ?? []) track.enabled = !muted;
  }

  // `async` EMAS: bu yerda kutadigan narsa yo'q. Interfeys `Promise`
  // talab qiladi, chunki LiveKit tarafda `disconnect()` asinxron.
  stop(): Promise<void> {
    this.closed = true;
    for (const track of this.local?.getTracks() ?? []) track.stop();
    this.pc?.close();
    this.pc = null;
    this.local = null;
    this.options.handlers.onState('closed');
    return Promise.resolve();
  }
}
